import { useCallback, useRef, useState } from "react";
import type {
  PremiseConfig,
  SchemeContent,
  SchemeKnobValues,
  TvCallView,
} from "@shared/types/foyer";
import type { TvRoomOfferFeed } from "@shared/types/roomOffer";
import {
  isCarePremise,
  isSchemeKey,
  type SchemeKey,
} from "@shared/types/foyer";
import { SCHEME_REGISTRY } from "./registry";
import { resolveScheme } from "./_shared/schemeContext";
import { defaultContentForScheme } from "@shared/schemes/content";
import { useGuestCasting } from "../hooks/useGuestCasting";
import { useRoomOffers } from "../hooks/useRoomOffers";
import { useTvCatalogue, type TvCatalogueHome } from "../hooks/useTvCatalogue";
import { useTvLineup, type TvLineup } from "../hooks/useTvLineup";
import { tvFeedWanted } from "./tvFeeds";
import { trackEngagement } from "../lib/engagement";
import { useRoomCallSession } from "../lib/callSession";
import IncomingCallOverlay from "./care/videocall/IncomingCallOverlay";

// Re-exported so existing TV imports (App.tsx) keep working after the key
// definitions moved into @shared/types/foyer.
export { isSchemeKey };
export type { SchemeKey };

interface Props {
  schemeKey: SchemeKey;
  config: PremiseConfig;
  roomId?: string;
  /**
   * Design-preview only: paint the incoming-call ring against a synthetic call
   * so it can actually be LOOKED at (`?scheme=care&call=ring`). Passed solely by
   * App's `?scheme=` branch - the real Launcher never sets it, so no bound TV can
   * be made to fake a ring.
   *
   * The call windows are the least reviewable surface in the product: they exist
   * for a few seconds, only during a real call, only on a real box. VC3-9 was a
   * design pass done blind for exactly that reason. This is the fix for the next
   * one.
   */
  previewCall?: "ring" | "announce" | "connecting" | "failed";
  /**
   * Design-preview only: force toggle knobs ON (`?scheme=care&on=vilmer,menu`).
   *
   * Every Information screen is opt-in and off by default, so a design preview
   * of one shows an empty menu unless the reviewer happens to have a premise
   * with that exact switch on. Same reasoning as `previewCall` above: a surface
   * that can only be looked at by first configuring a customer is a surface
   * that gets designed blind. Passed solely by App's `?scheme=` branch.
   */
  previewKnobs?: SchemeKnobValues;
  /**
   * Design-preview only: content merged OVER the demo content, so a screen that
   * is empty until an operator authors it can still be looked at
   * (`?scheme=care&on=custom&screen=custom`). Passed solely by App's `?scheme=`
   * branch - a bound TV never receives it, so no real room can be made to show
   * invented content.
   */
  previewContent?: Partial<SchemeContent>;
  /**
   * Design-preview only: a stand-in offers feed, so the Offers screen can be
   * looked at without a premise and without a backend
   * (`?scheme=<key>&on=offers&screen=offers`).
   *
   * Same rule as `previewContent`: passed solely by App's `?scheme=` branch, so
   * no bound television can be made to advertise merchandise nobody authored.
   */
  previewOffers?: TvRoomOfferFeed;
  /**
   * Design-preview only: a stand-in library and line-up, so the `vod` and
   * `guide` screens can be looked at without a premise, a pairing or a backend
   * (`?scheme=<key>&on=vod&screen=vod`).
   *
   * These two matter more than the others. Foyer production holds ZERO
   * `packages` rows, so a real bound television resolves every rail to
   * nothing - which means without a stand-in there is no configuration
   * anywhere, live or local, in which these screens can be reviewed by eye.
   *
   * Same rule as `previewContent`: passed solely by App's `?scheme=` branch,
   * which a bound television never takes.
   */
  previewVod?: TvCatalogueHome;
  previewGuide?: TvLineup;
  /**
   * CMS live preview only: a REAL room whose integration wiring the preview
   * should borrow.
   *
   * The preview mounts as `roomId="preview"`, which matches no row, so anything
   * resolved per room reports itself unconfigured — the VilMer pane said "inte
   * kopplade än" even for a premise whose rooms all carried tokens. An operator
   * checking their own work would have concluded the feature was broken.
   *
   * Deliberately narrow: it is read ONLY by the VilMer lookup, never by the
   * whiteboard, the photo slideshow or anything else keyed on a room. Those
   * read resident data, and pointing a preview at a real room would put PHI in
   * front of any CMS user who can open the template editor.
   */
  previewRoomId?: string;
}

/** A stand-in inbound call for the design preview. */
function previewCallView(kind: NonNullable<Props["previewCall"]>): TvCallView {
  return {
    callId: "preview",
    state: "ringing",
    initiatorKind: "phone",
    caller: {
      displayName: "Anna Lyngby",
      relation: "dotter",
      avatarUrl: null,
    },
    autoAnswer: kind === "announce",
    announceSeconds: kind === "announce" ? 20 : 0,
    startedAt: new Date(0).toISOString(),
    expiresAt: null,
  };
}

/**
 * Resolves a premise's scheme binding (or the demo fallback when none is set)
 * into a `ResolvedScheme` and renders the matching scheme component. The TV App
 * calls this when the premise has selected a scheme; otherwise the classic
 * page/tile renderer handles the screen.
 *
 * The premise stores a `scheme` block (content + token overrides + knob
 * choices). When it's absent - or its key doesn't match the requested scheme
 * (e.g. a `?scheme=` design preview) - we fall back to demo content keyed off
 * the premise's name/city so the scheme still reads as that property.
 */
/**
 * Does this premise need the offers feed at all?
 *
 * TWO switches reach the catalogue, and they are independent:
 *
 * - `showOffers` opens the Offers SCREEN in the menu row.
 * - `showPromotions` plus a slot filled with an `offer` puts ONE offer on the
 *   home screen as a card.
 *
 * An operator can reasonably want the second without the first: a featured
 * offer on the home screen and no extra item in the navigation. Reading only
 * `showOffers` meant that configuration fetched nothing, the slot resolved
 * against a feed that never arrived, and the fail-soft path built for a DELETED
 * offer swallowed it - no card, no error, nothing in the console. It looked
 * exactly like the feature not working.
 *
 * The `has()` guards stay: `toggle()` fails VISIBLE for a knob the manifest
 * never declared, so read naively every care premise would start polling a
 * hotel catalogue it is forbidden to have (`docs/room-offers.md` §10).
 */
export function offersFeedWanted(base: {
  knobs: { has: (id: string) => boolean; toggle: (id: string) => boolean };
  content: { slots?: Record<string, { type?: string }[] | undefined> };
}): boolean {
  if (base.knobs.has("showOffers") && base.knobs.toggle("showOffers")) {
    return true;
  }
  const promotionsOn =
    base.knobs.has("showPromotions") && base.knobs.toggle("showPromotions");
  if (!promotionsOn) return false;
  return Object.values(base.content.slots ?? {}).some((fills) =>
    (fills ?? []).some((fill) => fill?.type === "offer")
  );
}

export default function SchemeRouter({
  schemeKey,
  config,
  roomId,
  previewCall,
  previewKnobs,
  previewContent,
  previewOffers,
  previewVod,
  previewGuide,
  previewRoomId,
}: Props) {
  const { component: Component, manifest } = SCHEME_REGISTRY[schemeKey];
  const stored =
    config.scheme && config.scheme.key === schemeKey
      ? config.scheme
      : undefined;
  // The design preview forces switches on OVER whatever is stored, and needs a
  // binding to hang them on when there is none (the `?scheme=` stub config has
  // no scheme block at all). `defaultContentForScheme` is what resolveScheme
  // would have fallen back to anyway, so content is unchanged either way.
  const forced =
    (previewKnobs && Object.keys(previewKnobs).length) ||
    (previewContent && Object.keys(previewContent).length);
  const binding = forced
    ? {
        key: schemeKey,
        content: {
          ...(stored?.content ??
            defaultContentForScheme(schemeKey, {
              hotelName: config.premiseName || "The Hotel",
              city: config.address?.city,
            })),
          ...previewContent,
        },
        tokens: stored?.tokens,
        knobs: { ...stored?.knobs, ...previewKnobs },
      }
    : stored;
  const defaultLocale = config.defaultLocale || "en";
  // Active display language - a runtime override the on-screen language switcher
  // (globe icon) can change; starts at the premise default.
  const [locale, setLocale] = useState(defaultLocale);
  // A guest picking a language is a real engagement signal (and tells an
  // operator which languages a property actually needs), so the switcher goes
  // through a counted wrapper rather than raw setState.
  // `lastLocaleRef` mirrors `locale` but is readable synchronously, so the
  // dedupe can't be double-fired by React invoking a state updater twice in
  // development.
  const lastLocaleRef = useRef(defaultLocale);
  const chooseLocale = useCallback((next: string) => {
    if (lastLocaleRef.current !== next) {
      lastLocaleRef.current = next;
      trackEngagement("language_switch", next);
    }
    setLocale(next);
  }, []);
  // Per-room integration token (e.g. Vilmer) for embed URLs in the scheme.
  const integrationId = roomId
    ? config.rooms?.find((r) => r.id === roomId)?.integrationId
    : undefined;
  const base = resolveScheme({
    manifest,
    binding,
    fallbackContent: defaultContentForScheme(schemeKey, {
      hotelName: config.premiseName || "The Hotel",
      city: config.address?.city,
    }),
    locale,
    defaultLocale,
    premiseId: config.premiseId,
    roomId,
    previewRoomId,
    integrationId,
    supportedLocales: config.supportedLocales,
    setLocale: chooseLocale,
    lat: config.address?.lat,
    lng: config.address?.lng,
    products: config.products,
    // The premise's own choice of player, from the config this render is
    // actually using. `openTv` otherwise reads the durable cache keyed by the
    // pairing binding — which a CMS preview does not have.
    tvPlayer: config.tvPlayer,
    // A preview is anything the caller hands a catalogue to: the CMS live
    // preview and the `?scheme=` design preview both do, a bound television
    // never does. Read ONLY to show diagnostics an operator needs and a guest
    // must not see.
    preview: previewVod !== undefined || previewGuide !== undefined,
  });
  // Room offers (POS-3) live behind their own route rather than in the config,
  // so they are fetched here - the single entry to every scheme - and spread
  // onto the resolved scheme once they land.
  //
  // Asked for ONLY when this template declares the switch AND it is on. The
  // `has()` guard is the same one `showCast` needs two lines down and for the
  // same reason: `knobs.toggle()` fails VISIBLE for a knob the manifest never
  // declared, so read naively every care premise would start polling a hotel
  // catalogue it is forbidden to have (`docs/room-offers.md` §10).
  // ...and ALSO when a slot features one. The home card is switched by
  // `showPromotions`, not by `showOffers`: an operator can put a bookable offer
  // on the home screen WITHOUT opening the Offers screen in the menu row, and
  // that is a reasonable thing to want. Gating the feed on `showOffers` alone
  // meant the card silently rendered nothing in exactly that configuration -
  // the slot resolved against a feed that was never fetched, and fail-soft
  // (which exists for a DELETED offer) swallowed it with no error anywhere.
  const offers = useRoomOffers(
    offersFeedWanted(base) ? config.premiseId : undefined,
    previewOffers
  );
  // The library and the line-up (`TV-3a`/`TV-3b`/`TV-4`), on the same footing
  // as offers: their own routes, fetched here at the single entry to every
  // scheme, spread on once they land.
  //
  // Gated per screen and INDEPENDENTLY - a property can reasonably have films
  // and no channels, or channels and no films, and `showVod`/`showGuide` say
  // so separately. `tvFeedWanted` carries the `has()` guard that stops a care
  // premise polling a hotel catalogue it declares no knob for.
  const wantVod = tvFeedWanted(base, "showVod");
  const wantGuide = tvFeedWanted(base, "showGuide");
  const vodHome = useTvCatalogue(
    wantVod ? config.premiseId : undefined,
    // Both halves matter: the guest's chosen language, and the premise's own
    // default as the fallback. A title with no Swedish label must read as the
    // hotel's default rather than as a blank poster caption.
    { locale, defaultLocale },
    previewVod
  );
  const lineup = useTvLineup(previewGuide, wantGuide);
  // The WHOLE feed goes down, not a narrowed "menu view" of it. The screens
  // read the same object the content gate counts, so a menu can never again
  // show a section whose page then renders nothing - which is exactly what the
  // design preview did, and what any surface holding the data already would.
  const vod = vodHome.home;
  const guide = lineup;
  const scheme = {
    ...base,
    ...(offers ? { offers } : {}),
    ...(vod ? { vod } : {}),
    ...(guide ? { guide } : {}),
  };
  // The "Screen Share" toggle is also the guest-casting switch on the platforms
  // where firmware lets us throw it (MediaSuite: Secure Sharing + Cast).
  // Mounted here - the single entry to every scheme - so it runs on any page.
  //
  // Care premises have no screen sharing at all: no tile, nothing about it in
  // the CMS, and - this line - no receiver brought up behind their backs. That
  // needs saying explicitly because `knobs.toggle()` FAILS VISIBLE for a knob
  // the manifest never declared, and Hemvist declares no `showCast`: read
  // naively it resolved to `true` and switched Google Cast ON in care rooms.
  // The `has()` guard is deliberately NOT generalised to every template -
  // atelier/mosaic/spectrum declare no `showCast` either and hotels there rely
  // on the receiver staying up (they simply draw no tile for it).
  const castingOn =
    !isCarePremise(config) &&
    (!scheme.knobs.has("showCast") || scheme.knobs.toggle("showCast"));
  useGuestCasting(castingOn);

  // VC-27: the incoming-call ring lives HERE, not inside CareScheme, so it
  // paints above every scheme screen - the slideshow, the whiteboard, the
  // radio, live TV - not just the care launcher. Video calls are a care
  // feature, so we only subscribe on the care scheme; every other premise gets
  // a permanently quiet room and never opens a call connection.
  //
  // A `room`-initiated session is the TV's OWN outgoing call, which CareScheme's
  // OutboundCall already renders as "Ringer…"; we must never paint an incoming
  // ring for it, so it is filtered out before the overlay ever mounts.
  const { call, clear } = useRoomCallSession({ enabled: schemeKey === "care" });
  const inbound = call && call.initiatorKind !== "room" ? call : null;

  /**
   * A ring the resident still has something to READ outlives the session it
   * belonged to.
   *
   * The overlay is mounted off `call`, so the instant the session ends the
   * component is gone - which is fine for a normal hang-up and wrong for a
   * failure: an answer that could not be completed ends the call as part of
   * failing (so the caller's phone is released), and that would wipe the very
   * message explaining what happened. The resident would see the ring vanish
   * and the main menu return, learning nothing - the pilot symptom of
   * 2026-07-27.
   *
   * So the overlay can ask to be HELD: it keeps rendering, against the last call
   * we knew about, until the resident acknowledges. A new ring always wins over
   * a held card - a real incoming call must never be blocked by an old notice.
   */
  const [held, setHeld] = useState<TvCallView | null>(null);
  const shown =
    inbound ?? held ?? (previewCall ? previewCallView(previewCall) : null);
  const dismiss = useCallback(
    (callId?: string) => {
      setHeld(null);
      clear(callId);
    },
    [clear]
  );

  return (
    <>
      <Component scheme={scheme} />
      {shown && (
        <IncomingCallOverlay
          key={shown.callId}
          call={shown}
          // The ring is mounted OUTSIDE the scheme provider (that is what lets
          // it paint above every screen), so it cannot read tokens from context.
          // Handing them down keeps a premise's colour overrides flowing into
          // the one window a resident sees most (VC3-9).
          tokens={scheme.tokens}
          previewPhase={previewCall === "ring" ? undefined : previewCall}
          onDismiss={dismiss}
          onHold={() => setHeld(shown)}
        />
      )}
    </>
  );
}
